import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  WorkflowAutoCreationDayFilter,
  WorkflowAutoCreationLeadUnit,
} from '@ebalance/proto/bffuser/v1/workflow_auto_creation_rule_model_pb'
import type { TFunction } from 'i18next'
import { z } from 'zod'

import {
  type AutoCreationRuleRow,
  parseTimeOfDayHm,
} from '~/pages/AdminWorkflowTemplates/models/autoCreationRule'

const V = 'page:adminWorkflowTemplates.autoCreationRule.validation'

/** proto の int32 の上限。業務上の最大値は PdM確認 #12430 の回答で決める。 */
const INT32_MAX = 2_147_483_647

export type AutoCreationRuleFormValues = {
  templateId: string
  areaCodes: AreaCode[]
  /** 入力欄の文字列のまま持ち、送信時に数値にする。 */
  leadValue: string
  leadUnit: WorkflowAutoCreationLeadUnit
  /** `HH:mm` (JST)。 */
  fireTimeHm: string
  dayFilter: WorkflowAutoCreationDayFilter
  enabled: boolean
}

/** 定期のルールの保存前の検査 (AC-WFT-052)。 */
export function createAutoCreationRuleFormSchema(
  t: TFunction,
): z.ZodType<AutoCreationRuleFormValues, AutoCreationRuleFormValues> {
  return z.object({
    templateId: z.string().min(1, t(`${V}.templateRequired`)),
    areaCodes: z.array(z.nativeEnum(AreaCode)).min(1, t(`${V}.areaRequired`)),
    leadValue: z
      .string()
      .refine(
        (v) => /^\d+$/.test(v.trim()) && Number(v.trim()) <= INT32_MAX,
        t(`${V}.leadInvalid`),
      ),
    leadUnit: z.nativeEnum(WorkflowAutoCreationLeadUnit),
    fireTimeHm: z
      .string()
      .refine(
        (v) => parseTimeOfDayHm(v) !== undefined,
        t(`${V}.fireTimeRequired`),
      ),
    dayFilter: z.nativeEnum(WorkflowAutoCreationDayFilter),
    enabled: z.boolean(),
  })
}

/**
 * 新規の既定値 (AC-WFT-021)。起票リードは雛形を選ぶまでの仮の値で、雛形を選ぶと
 * `defaultLeadForSection` の値に入れ替える。
 */
export function defaultAutoCreationRuleFormValues(): AutoCreationRuleFormValues {
  return {
    templateId: '',
    areaCodes: [],
    leadValue: '1',
    leadUnit: WorkflowAutoCreationLeadUnit.DAY,
    fireTimeHm: '00:00',
    dayFilter: WorkflowAutoCreationDayFilter.EVERY_DAY,
    enabled: true,
  }
}

export function autoCreationRuleFormValuesFromRow(
  row: AutoCreationRuleRow,
): AutoCreationRuleFormValues {
  return {
    templateId: row.template.id,
    areaCodes: [row.areaCode],
    leadValue: String(row.schedule.leadValue),
    leadUnit: row.schedule.leadUnit,
    fireTimeHm: row.schedule.fireTimeHm,
    dayFilter: row.schedule.dayFilter,
    enabled: row.enabled,
  }
}
